import { WorkspaceInvitationPolicy, WorkspaceTier } from "./local/Types";
import { User, UserSimplified, toUserSimplified } from "./User";
import { CompanyInfo, BillingInfo, SalesInfo, WorkspaceOverview } from "./WorkspaceAdmin";
import { WorkspacePermissions, RolePermissionsMap, DEFAULT_ROLE_PERMISSIONS, resolvePermissions } from "./WorkspacePermissions";

// Role types for workspace members
export type WorkspaceMemberRole = 'owner' | 'admin' | 'member' | 'guest';

// Re-export for convenience
export { toUserSimplified };

export class Workspace {
  static COL = 'workspaces';
  created: Date = new Date();
  tier: WorkspaceTier = 'FREE';
  adminIds: string[] = [];
  description?: string;
  invitationKey?: string;
  invitationPolicy?: WorkspaceInvitationPolicy = 'INVITE_ONLY';
  guestInvitationPolicy?: 'ALLOW_ALL' | 'RESTRICT_TO_DOMAINS' | 'DISALLOW_ALL' = 'ALLOW_ALL';
  emailDomains: string[] = [];
  guestDomains: string[] = [];
  autoJoinEmailDomains: string[] = [];
  iconUrl?: string | null;

  // Minimal owner info (for UI display) - use owner.id for owner ID
  owner: UserSimplified;

  // 역할별 기본 권한 템플릿
  rolePermissions: RolePermissionsMap = { ...DEFAULT_ROLE_PERMISSIONS };

  // 슈퍼어드민 관리용 필드 (Phase 0)
  companyInfo?: CompanyInfo;           // 회사정보 (사용자 입력)
  billingInfo?: BillingInfo;           // 청구정보 (슈퍼어드민 관리)
  salesInfo?: SalesInfo;               // 영업정보 (슈퍼어드민 관리)
  overview?: WorkspaceOverview;        // Overview 캐시 (Cloud Functions 관리)

  constructor(public id: string, public name: string, owner: User | UserSimplified) {
    this.owner = toUserSimplified(owner);
    this.adminIds = [owner.id];
  }
}

/** 가입 정책 판정에 필요한 최소 필드 (Firestore data() POJO도 그대로 사용 가능) */
export type WorkspaceJoinPolicyFields = Pick<Workspace, 'invitationPolicy' | 'emailDomains'>;

/** invitationPolicy 미설정(레거시 문서)은 비공개(INVITE_ONLY)로 간주 */
export function getInvitationPolicy(ws: WorkspaceJoinPolicyFields): WorkspaceInvitationPolicy {
  return ws.invitationPolicy ?? 'INVITE_ONLY';
}

/** 이메일 도메인이 멤버 허용 도메인 목록에 있는지 — 목록이 비어 있으면 아무도 허용되지 않음 */
export function isMemberDomainAllowed(ws: WorkspaceJoinPolicyFields, email: string | null | undefined): boolean {
  const domain = email?.split('@')[1]?.toLowerCase() ?? '';
  if (!domain) return false;
  return (ws.emailDomains ?? []).some(d => d.toLowerCase() === domain);
}

/**
 * 멤버로 가입(게스트→멤버 승격 포함) 가능 여부 — 서버/클라이언트 공용 단일 규칙.
 * - ALLOW_ANY_EMAIL_DOMAIN(공개): 누구나 가입 (ID 검색 포함)
 * - ALLOW_LISTED_EMAIL_DOMAIN(도메인 제한): 유효한 초대 링크 + 허용 도메인 모두 필요 (ID 검색 가입 불가)
 * - INVITE_ONLY(비공개, 기본): 유효한 초대 링크로만 가입 (ID 검색 가입 불가)
 */
export function canJoinAsMember(ws: WorkspaceJoinPolicyFields, email: string | null | undefined, hasValidInvitationKey: boolean): boolean {
  switch (getInvitationPolicy(ws)) {
    case 'ALLOW_ANY_EMAIL_DOMAIN':
      return true;
    case 'ALLOW_LISTED_EMAIL_DOMAIN':
      return hasValidInvitationKey && isMemberDomainAllowed(ws, email);
    case 'INVITE_ONLY':
    default:
      return hasValidInvitationKey;
  }
}

// Workspace Member - with role support
export class WorkspaceMember {
  static COL = 'members'; //workspaces/members
  id: string;
  created: Date = new Date();

  // Role for access control
  role: WorkspaceMemberRole = 'member';

  // 비정규화된 최종 권한 (resolvePermissions로 계산)
  permissions: WorkspacePermissions;

  // 개별 오버라이드 (Phase 2 — null이면 미사용)
  overrides: Partial<WorkspacePermissions> | null = null;

  // 소속 권한 그룹 ID 목록 (Phase 3 — 기본 빈 배열)
  groupIds: string[] = [];

  // Custom quota override (optional)
  customQuotaBytes?: number;

  // Minimal user info (for UI display) - use user.id for user ID
  user: UserSimplified;

  // Workspace reference (for legacy queries)
  workspace?: Workspace;

  constructor(workspace: Workspace, user: User | UserSimplified, role: WorkspaceMemberRole = 'member') {
    this.user = toUserSimplified(user);
    this.id = user.id;
    this.role = role;
    // 생성 시 permissions 계산 (workspace는 계산용으로만 사용, 문서에 저장하지 않음)
    this.permissions = resolvePermissions(role, workspace.rolePermissions ?? null);
  }
}
